import {
  ChakraBox,
  ChakraFlex,
  ChakraSimpleGrid,
  Divider,
  FAIconName,
  FireflyButton,
  GooglePlacesAutoCompleteV2,
  InputGroupV2,
  Text,
  TextInputV2,
} from '@equip.health/ui';

import { VITE_GOOGLE_PLACES_API_KEY } from '~/lib/constants/env';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import { Address } from '~/lib/constants/patients.constants';
import useCustomShippingAddress from '~/lib/hooks/useCustomShippingAddress';
import { isAllAddressFieldsNotEmpty } from '~/lib/util/clinicalProfile.util';
import {
  getRequiredFields,
  validatePhoneNumber,
} from '~/lib/util/patient.util';
import {
  formatPhoneNumberWithCode,
  isValidZipCodeFormat,
} from '~/lib/util/patientContact.util';

const TOP_LEVEL_ID = 'enter-address-manually';

interface CustomShippingAddressProps {
  onBack: () => void;
  onUseAddress: (address: Address) => void;
  addressData: Address[];
  selectedAddress?: Address;
}
const CustomShippingAddress = ({
  onBack,
  onUseAddress,
  addressData,
  selectedAddress,
}: CustomShippingAddressProps) => {
  const {
    address,
    stateOptions,
    isEmailValid,
    isFormValid,
    isGetStatesLoading,
    handleAddressChange,
    isShippingAddressFieldEmpty,
    isShippingAddressAllFieldEmpty,
  } = useCustomShippingAddress(addressData, selectedAddress);

  const handleUseAddressClick = () => {
    const combinedAddress = {
      ...address,
      name: `${address.firstName} ${address.lastName}`,
    };
    onUseAddress(combinedAddress);
  };
  const allAddressesValid = addressData.every(isAllAddressFieldsNotEmpty);

  return (
    <ChakraFlex flexDirection="column" gap="24px" marginTop="6px">
      <ChakraFlex flexDirection="column" gap="12px">
        <Text color="neutral.primary" variant="bodyMedium">
          Contact Details
        </Text>
        <ChakraSimpleGrid
          columnGap="24px"
          columns={2}
          rowGap="12px"
          width="100%"
        >
          <InputGroupV2 id={`${TOP_LEVEL_ID}__first`} label="First">
            <TextInputV2
              id={`${TOP_LEVEL_ID}__first-name-input`}
              isInvalid={!address.firstName}
              onChange={(value) => handleAddressChange('firstName', value)}
              value={address.firstName}
            />
          </InputGroupV2>
          <InputGroupV2 id={`${TOP_LEVEL_ID}__last`} label="Last">
            <TextInputV2
              id={`${TOP_LEVEL_ID}__last-name - input}`}
              isInvalid={!address.lastName}
              onChange={(value) => handleAddressChange('lastName', value)}
              value={address.lastName}
            />
          </InputGroupV2>
          <InputGroupV2 id={`${TOP_LEVEL_ID}__email`} label="Email">
            <TextInputV2
              id={`${TOP_LEVEL_ID}__email-input`}
              isInvalid={!isEmailValid || !address.email}
              leftIcon={FAIconName.envelopeRegular}
              onChange={(value) => handleAddressChange('email', value)}
              type={TextInputV2Type.EMAIL}
              value={address.email}
            />
          </InputGroupV2>
          <InputGroupV2 id={`${TOP_LEVEL_ID}__phone`} label="Phone">
            <TextInputV2
              ariaLabel="Phone"
              id={`${TOP_LEVEL_ID}__phone-input`}
              isInvalid={
                !address.phoneNumber ||
                !validatePhoneNumber(address?.phoneNumber)
              }
              leftIcon={FAIconName.mobileRegular}
              onChange={(value) => handleAddressChange('phoneNumber', value)}
              type={TextInputV2Type.PHONE_CODE}
              value={formatPhoneNumberWithCode(address?.phoneNumber)}
            />
          </InputGroupV2>
        </ChakraSimpleGrid>
        <Divider id="divider" />
      </ChakraFlex>

      <ChakraFlex flexDirection="column" gap="12px">
        <Text color="neutral.primary" variant="bodyMedium">
          Address
        </Text>
        <ChakraBox>
          <GooglePlacesAutoCompleteV2
            addressData={address}
            apiKey={VITE_GOOGLE_PLACES_API_KEY}
            id={`${TOP_LEVEL_ID}__address`}
            isGetStatesLoading={isGetStatesLoading}
            label=""
            requiredFields={
              isShippingAddressAllFieldEmpty ? [] : getRequiredFields()
            }
            saveAddressData={(data: GooglePlacesField) =>
              handleAddressChange('address', data)
            }
            shouldShowOrganizationName={false}
            shouldShowTimezone={false}
            showAddressFields={
              selectedAddress && Object.keys(selectedAddress).length > 0
                ? true
                : false
            }
            showUseMainAddressCheckbox={false}
            stateOptions={stateOptions}
            timezoneOptions={[]}
            updateAddress={(
              value: string | { value: string; label: string },
              field: string,
            ) => {
              const fieldValue =
                typeof value === 'object' ? value?.value : value;
              const updatedData: GooglePlacesField = {
                ...address,
                [field]: fieldValue,
                country: '',
              } as GooglePlacesField;
              handleAddressChange('address', updatedData);
            }}
          />
        </ChakraBox>
      </ChakraFlex>
      <ChakraFlex justifyContent="space-between">
        <FireflyButton
          id={`${TOP_LEVEL_ID}__address-manually-back-button`}
          isDisabled={!allAddressesValid}
          leftIcon={FAIconName.chevronLeftRegular}
          onClick={onBack}
          variant="secondary"
        >
          Back
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__use-this-address-button`}
          isDisabled={
            !isFormValid ||
            isShippingAddressFieldEmpty ||
            !isValidZipCodeFormat(address?.zipCode)
          }
          leftIcon={FAIconName.checkRegular}
          onClick={handleUseAddressClick}
          variant="primary"
        >
          Use this Address
        </FireflyButton>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default CustomShippingAddress;
